import * as React from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { CloseButton, Form, Pagination, Table } from 'react-bootstrap';
import { getLocalization } from 'global/global';
import EditableCell from './EditableCell';

interface Props<T extends { id: number }> {
  data: T[];
  columns: ColumnDef<T>[];
  selection: number[];
  onSelectionChange: (ids: number[]) => void;
  onEdit: (row: T, field: string, value: string | boolean) => void;
  /** Rendered above the table: title on the left, actions on the right. */
  title: string;
  actions?: React.ReactNode;
  onClose?: () => void;
}

const PAGE_SIZES = [5, 10, 20];

/**
 *  The admin grid, replacing jqxGrid. Supports the same interactions the admin
 *  tool relied on: checkbox selection, per-column filtering, sorting, paging and
 *  edit-in-place on double click.
 */
export default function DataTable<T extends { id: number }>(props: Props<T>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [filters, setFilters] = React.useState<Record<string, string>>({});

  /** TanStack keys selection by row index; the admin code works in ids. */
  const rowSelection: RowSelectionState = React.useMemo(() => {
    const state: RowSelectionState = {};
    props.data.forEach((row, index) => {
      if (props.selection.includes(row.id)) {
        state[index] = true;
      }
    });
    return state;
  }, [props.data, props.selection]);

  const table = useReactTable({
    data: props.data,
    columns: props.columns,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange: (updater) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      props.onSelectionChange(
        Object.keys(next)
          .filter((index) => next[index])
          .map((index) => props.data[Number(index)]?.id)
          .filter((id) => id !== undefined),
      );
    },
    enableRowSelection: true,
    defaultColumn: { sortingFn: 'alphanumeric' },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  React.useEffect(() => {
    Object.entries(filters).forEach(([id, value]) => {
      table.getColumn(id)?.setFilterValue(value || undefined);
    });
  }, [filters]);

  const rows = table.getRowModel().rows;
  const allSelected = rows.length > 0 && rows.every((r) => r.getIsSelected());

  return (
    <div className="admin-grid">
      <div className="admin-grid-toolbar">
        <strong>{props.title}</strong>
        <span className="admin-grid-actions">
          {props.actions}
          {props.onClose && (
            <CloseButton
              onClick={props.onClose}
            />
          )}
        </span>
      </div>

      <Table striped bordered hover size="sm" responsive>
        <thead>
          <tr>
            <th className="admin-grid-select">
              <Form.Check
                aria-label="select-all"
                checked={allSelected}
                onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
              />
            </th>
            {table.getFlatHeaders().map((header) => (
              <th
                key={header.id}
                style={{ width: (header.column.columnDef.meta as { width?: number })?.width }}
              >
                <div role="button" onClick={header.column.getToggleSortingHandler()}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {{ asc: ' ▲', desc: ' ▼' }[header.column.getIsSorted() as string] || ''}
                </div>
                <Form.Control
                  size="sm"
                  aria-label={`filter-${header.column.id}`}
                  value={filters[header.column.id] || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, [header.column.id]: e.target.value }))}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="admin-grid-select">
                <Form.Check
                  aria-label={`select-${row.original.id}`}
                  checked={row.getIsSelected()}
                  onChange={(e) => row.toggleSelected(e.target.checked)}
                />
              </td>
              {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta as {
                  editable?: 'text' | 'checkbox' | 'select';
                  options?: { label: string; value: string }[];
                } | undefined;
                return (
                  <td key={cell.id}>
                    <EditableCell
                      value={cell.getValue() as string | number | boolean | null}
                      editable={meta?.editable}
                      options={meta?.options}
                      rendered={flexRender(cell.column.columnDef.cell, cell.getContext())}
                      onCommit={(value) => props.onEdit(row.original, cell.column.id, value)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={table.getAllColumns().length + 1}>{getLocalization('tableEmpty')}</td>
            </tr>
          )}
        </tbody>
      </Table>

      <div className="admin-grid-pager">
        <Pagination size="sm">
          <Pagination.Prev
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          />
          <Pagination.Item disabled>
            {table.getState().pagination.pageIndex + 1} / {Math.max(1, table.getPageCount())}
          </Pagination.Item>
          <Pagination.Next
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          />
        </Pagination>
        <span>
          {getLocalization('showRows')}
          <Form.Control
            as="select"
            size="sm"
            aria-label="page-size"
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
          </Form.Control>
        </span>
      </div>
    </div>
  );
}
